import { useEffect, useMemo, useState } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClientProvider } from '@tanstack/react-query';
import { SiteTrackingPlugin } from '@snowplow/browser-plugin-site-tracking';
import { hasUserAcceptedTracking } from '@pkgs/components/utils/tracking';
import { getSortingOptionIdFromFilters, isNonEmptyObject, resolveInitialSortingOptionId } from '@pkgs/components/utils/searchStateUtils';
import { mapSearchListingsFiltersToSearchState, prepareSearchListingsFiltersForRequest } from '@pkgs/components/utils/mapFilterFields';
import { type SearchModuleProps } from '@pkgs/components/types/searchModuleTypes';
import { UserAccountProvider } from '@pkgs/components/providers/UserAccountProvider';
import { SearchWatchlistProvider } from '@pkgs/components/providers/SearchWatchlistProvider';
import { SearchEffectsProvider } from '@pkgs/components/providers/SearchEffectsProvider';
import { MapVisibilityStoreProvider } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { BreakpointProvider } from '@pkgs/components/providers/BreakpointProvider';
import { AppContextProvider } from '@pkgs/components/providers/AppContextProvider';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { SearchStoreProvider } from '@pkgs/components/hooks/stores/search/SearchStoreProvider';
import {
  getAgentProfileQueryKey,
  getGeoCoordinatesQueryKey,
  getListingsQueryKey,
  getPlaceDetailsQueryKey,
} from '@pkgs/components/hooks/queries/queryKeys';
import { createQueryClient } from '@pkgs/components/hooks/queries/queryClient';
import { DEFAULT_FEATURE_FLAGS } from '@pkgs/components/consts/consts';
import { StatusMessageProvider } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { initSnowplowTracker } from '@ev-tech/snowplow-library';
import { type PlaceDetails } from '@ev-tech/search-modules-api';

import { SearchResults } from '../SearchResults/SearchResults';
import { FilterBar } from '../FilterBar/FilterBar';

import styles from './SearchModule.module.css';

export const SearchModule = (props: SearchModuleProps) => {
  const {
    tracking,
    initialFilters,
    initialResults,
    initialGeoResults,
    initialAgentProfile,
    options,
    initialPlaceDetails,
    initialPlaceName,
    watchlist,
    events,
    autogenConfig,
    userAccountProps,
    countryCode,
    queryDevtoolsEnabled = false,
    initialSearchMode,
    transComponent,
    featureFlags = DEFAULT_FEATURE_FLAGS,
    isMapToggledOnInitially,
    dataTestId,
    initialSortingOptionId,
    smartSortPreferences,
  } = props;
  const [isSnowplowInitialized, setIsSnowplowInitialized] = useState(Boolean(tracking?.isSnowplowInitialized));
  const hasSmartSortPreferences = isNonEmptyObject(smartSortPreferences);
  const hasAiSearchPreferences = isNonEmptyObject(initialFilters?.preferences);

  const resolvedInitialSortingOptionId = useMemo(
    () =>
      resolveInitialSortingOptionId({
        sortingOptionId:
          initialSortingOptionId ??
          getSortingOptionIdFromFilters({
            sortingOptions: initialFilters?.sortingOptions,
            searchMode: initialSearchMode,
          }),
        propertyMarketingType: initialFilters?.propertyMarketingType[0],
        searchMode: initialSearchMode,
        isSmartSortEnabled: featureFlags.isSmartSortEnabled ?? false,
        hasSmartSortPreferences,
        hasAiSearchPreferences,
      }),
    [
      featureFlags.isSmartSortEnabled,
      hasAiSearchPreferences,
      hasSmartSortPreferences,
      initialFilters,
      initialSearchMode,
      initialSortingOptionId,
    ],
  );

  const initialRequestFilters = useMemo(() => {
    if (!initialFilters) return undefined;

    return prepareSearchListingsFiltersForRequest({
      filters: initialFilters,
      sortingOptionId: resolvedInitialSortingOptionId,
      smartSortPreferences,
    });
  }, [initialFilters, resolvedInitialSortingOptionId, smartSortPreferences]);

  const queryClient = useMemo(() => {
    if (props.queryClient) return props.queryClient;

    const queryClient = createQueryClient();
    if (!initialRequestFilters) return queryClient;

    if (initialResults) {
      queryClient.setQueryData(
        getListingsQueryKey({
          filters: initialRequestFilters,
          options,
        }),
        initialResults,
      );
    }
    if (initialGeoResults) {
      queryClient.setQueryData(getGeoCoordinatesQueryKey({ filters: initialRequestFilters, options }), initialGeoResults);
    }
    if (initialPlaceDetails) {
      for (const placeDetails of initialPlaceDetails) {
        queryClient.setQueryData(getPlaceDetailsQueryKey({ placeId: placeDetails.placeId, language: options.language }), placeDetails);
      }
    }
    if (initialAgentProfile) {
      queryClient.setQueryData(getAgentProfileQueryKey({ agentId: initialRequestFilters.agentId }), initialAgentProfile);
    }

    return queryClient;
    // Reason: keep this dependency list minimal so QueryClient stays stable. A new instance remounts
    // QueryClientProvider and clears optimistic updates (e.g. favouriting briefly shows as saved, then reverts).
  }, [props.queryClient]);

  useEffect(() => {
    if (tracking?.isSnowplowInitialized) return;

    if (!tracking?.snowplowApplicationId) {
      console.warn('Snowplow application ID is not provided.');
      return;
    }
    initSnowplowTracker({
      applicationId: tracking.snowplowApplicationId,
      hasUserAcceptedAgreement: hasUserAcceptedTracking(),
      isProd: window.location.host.includes('engelvoelkers.com'),
      initialTrackPageViewEvent: false,
      plugins: [SiteTrackingPlugin()],
    });
    setIsSnowplowInitialized(true);
  }, [tracking]);

  const placeName = useMemo(() => {
    if (!initialFilters?.placeIds) return '';

    return (
      initialPlaceName ??
      initialPlaceDetails?.[0]?.placeName ??
      queryClient.getQueryData<PlaceDetails>(getPlaceDetailsQueryKey({ placeId: initialFilters.placeIds[0], language: options.language }))
        ?.placeName ??
      ''
    );
  }, [initialPlaceDetails, initialPlaceName, initialFilters?.placeIds, options.language, queryClient]);
  return (
    <QueryClientProvider client={queryClient}>
      {queryDevtoolsEnabled && <ReactQueryDevtools initialIsOpen={false} />}
      <BreakpointProvider>
        <AppContextProvider {...props} tracking={{ ...tracking, isSnowplowInitialized }}>
          <SearchStoreProvider
            initialState={{
              ...(initialFilters && mapSearchListingsFiltersToSearchState({ filters: initialFilters, options, placeName })),
              searchMode: featureFlags.isAiSearchEnabled && initialSearchMode === SearchMode.AI ? SearchMode.AI : SearchMode.CLASSIC,
              modalSearchMode: featureFlags.isAiSearchEnabled && initialSearchMode === SearchMode.AI ? SearchMode.AI : SearchMode.CLASSIC,
              sortingOptionId: resolvedInitialSortingOptionId,
            }}
            onBeforeFiltersChange={events?.onBeforeFiltersChange}
          >
            <MapVisibilityStoreProvider onIsMapViewChange={events?.onIsMapViewChange} isMapToggledOnInitially={isMapToggledOnInitially}>
              <SearchEffectsProvider>
                <StatusMessageProvider>
                  <UserAccountProvider
                    translationResolver={userAccountProps.translationResolver}
                    countryCode={countryCode}
                    language={options.language}
                    transComponent={transComponent}
                    tracking={{
                      pageType: tracking?.pageType ?? '',
                      templateType: tracking?.templateType ?? '',
                      shopId: initialFilters?.shopIds?.[0] ?? initialFilters?.masterDataShopIds?.[0],
                    }}
                  >
                    <SearchWatchlistProvider watchlist={watchlist} onWatchlistClick={events?.onWatchlistClick}>
                      <div className={styles.wrapper} data-testid={dataTestId}>
                        <FilterBar />
                        <SearchResults autogenConfig={autogenConfig} />
                      </div>
                    </SearchWatchlistProvider>
                  </UserAccountProvider>
                </StatusMessageProvider>
              </SearchEffectsProvider>
            </MapVisibilityStoreProvider>
          </SearchStoreProvider>
        </AppContextProvider>
      </BreakpointProvider>
    </QueryClientProvider>
  );
};
